export type EventCategory =
  | 'retail' |'infrastructure' |'sports' |'education' |'entertainment' |'construction' |'government' |'healthcare' |'disaster';

export type EventStatus = 'live' | 'upcoming' | 'confirmed' | 'rumored';

export interface MappedBrand {
  name: string;
  logo: string;
  vertical: string;
  fitScore: number;
  reason: string;
}

export interface RegionalEvent {
  id: string;
  title: string;
  category: EventCategory;
  status: EventStatus;
  location: string;
  region: string;
  date: string;
  description: string;
  impactRadius: string;
  footfallEstimate: string;
  source: string;
  mappedVerticals: string[];
  mappedBrands: MappedBrand[];
}

export const regionalEvents: RegionalEvent[] = [
  {
    id: 'evt-001',
    title: 'Target Opens New Flagship Store',
    category: 'retail',
    status: 'confirmed',
    location: 'Bemidji, MN',
    region: 'Northern Minnesota',
    date: 'May 12, 2026',
    description: 'Target is opening a new 120,000 sq ft flagship store in the Bemidji Town Center, expected to draw 3,000+ daily visitors from surrounding counties.',
    impactRadius: '45 miles',
    footfallEstimate: '3,200/day',
    source: 'Bemidji Pioneer',
    mappedVerticals: ['qsr', 'automotive', 'finance', 'telecom'],
    mappedBrands: [
      { name: "McDonald's", logo: 'MC', vertical: 'QSR', fitScore: 94, reason: 'Drive-thru traffic surge near new retail anchor' },
      { name: 'Kwik Trip', logo: 'KT', vertical: 'Convenience', fitScore: 91, reason: 'Fuel & convenience for new shopper traffic corridor' },
      { name: 'T-Mobile', logo: 'TM', vertical: 'Telecom', fitScore: 88, reason: 'Device upgrade campaigns targeting new retail visitors' },
      { name: 'US Bank', logo: 'US', vertical: 'Finance', fitScore: 85, reason: 'Credit card and banking awareness near new retail hub' },
    ],
  },
  {
    id: 'evt-003',
    title: 'Bemidji Lumberjacks — Home Playoff Run',
    category: 'sports',
    status: 'live',
    location: 'Sanford Center, Bemidji',
    region: 'Northern Minnesota',
    date: 'Apr 8 – May 3, 2026',
    description: 'The Bemidji Lumberjacks have qualified for the USHL playoffs. 6 home games expected, drawing 4,500+ fans per game from a 100-mile radius. Sellout crowds anticipated.',
    impactRadius: '100 miles',
    footfallEstimate: '4,500/game day',
    source: 'USHL Official Schedule',
    mappedVerticals: ['qsr', 'retail', 'events', 'telecom'],
    mappedBrands: [
      { name: 'Kwik Trip', logo: 'KT', vertical: 'Convenience', fitScore: 97, reason: 'Pre-game fuel and snack runs on game day corridors' },
      { name: "McDonald's", logo: 'MC', vertical: 'QSR', fitScore: 94, reason: 'Post-game family dining — proven sports event pattern' },
      { name: 'Ticketmaster', logo: 'TM', vertical: 'Events', fitScore: 90, reason: 'Cross-sell other regional events to playoff attendees' },
      { name: 'Verizon', logo: 'VZ', vertical: 'Telecom', fitScore: 86, reason: 'Live game streaming and connectivity messaging' },
    ],
  },
  {
    id: 'evt-004',
    title: 'Northland Community College — Fall Enrollment Push',
    category: 'education',
    status: 'upcoming',
    location: 'Thief River Falls, MN',
    region: 'Northwest Minnesota',
    date: 'Aug 1 – Sep 15, 2026',
    description: 'Northland Community College launches its largest enrollment campaign, targeting 18-24 year olds across a 5-county region. New trades and healthcare programs added for fall 2026.',
    impactRadius: '60 miles',
    footfallEstimate: '1,800 prospective students',
    source: 'Northland CC Press Release',
    mappedVerticals: ['education', 'retail', 'telecom', 'finance'],
    mappedBrands: [
      { name: 'Rasmussen University', logo: 'RU', vertical: 'Education', fitScore: 92, reason: 'Competitive enrollment — target same audience with online options' },
      { name: 'T-Mobile', logo: 'TM', vertical: 'Telecom', fitScore: 89, reason: 'Student device and data plan campaigns during enrollment' },
      { name: 'Target', logo: 'TG', vertical: 'Retail', fitScore: 85, reason: 'Back-to-school supply and dorm essentials push' },
      { name: 'Alerus Financial', logo: 'AL', vertical: 'Finance', fitScore: 81, reason: 'Student banking and loan awareness during enrollment season' },
    ],
  },
  {
    id: 'evt-005',
    title: 'Walmart Supercenter — Ground Breaking',
    category: 'retail',
    status: 'confirmed',
    location: 'Park Rapids, MN',
    region: 'Central Minnesota',
    date: 'Jun 3, 2026',
    description: 'Walmart breaks ground on a new 185,000 sq ft Supercenter in Park Rapids, expected to open Q1 2027. Construction phase will bring 200+ workers to the area daily.',
    impactRadius: '55 miles',
    footfallEstimate: '200 workers/day (construction)',
    source: 'Park Rapids Enterprise',
    mappedVerticals: ['qsr', 'automotive', 'home', 'finance'],
    mappedBrands: [
      { name: 'Casey\'s General Store', logo: 'CG', vertical: 'Convenience', fitScore: 93, reason: 'Construction crew fuel and food runs — daily captive audience' },
      { name: 'NAPA Auto Parts', logo: 'NA', vertical: 'Automotive', fitScore: 88, reason: 'Construction vehicle maintenance and parts demand' },
      { name: 'Menards', logo: 'MN', vertical: 'Home Improvement', fitScore: 85, reason: 'Contractor supply runs during construction phase' },
      { name: 'Bremer Bank', logo: 'BB', vertical: 'Finance', fitScore: 80, reason: 'Business banking for new commercial development zone' },
    ],
  },
  {
    id: 'evt-006',
    title: 'Bemidji School District — Back to School',
    category: 'education',
    status: 'upcoming',
    location: 'Bemidji, MN',
    region: 'Northern Minnesota',
    date: 'Aug 25, 2026',
    description: 'Bemidji Area Schools reopens with 4,200 students across 8 schools. New school year brings peak family shopping activity across the region for 3 weeks.',
    impactRadius: '30 miles',
    footfallEstimate: '4,200 students + families',
    source: 'Bemidji Area Schools Calendar',
    mappedVerticals: ['retail', 'grocery', 'qsr', 'telecom'],
    mappedBrands: [
      { name: 'Target', logo: 'TG', vertical: 'Retail', fitScore: 96, reason: 'Back-to-school supplies — peak family shopping season' },
      { name: 'Cub Foods', logo: 'CF', vertical: 'Grocery', fitScore: 92, reason: 'Lunch prep and family meal planning for school year' },
      { name: 'Subway', logo: 'SB', vertical: 'QSR', fitScore: 88, reason: 'After-school meal and snack runs near school corridors' },
      { name: 'T-Mobile', logo: 'TM', vertical: 'Telecom', fitScore: 84, reason: 'Student device upgrades and family plan promotions' },
    ],
  },
  {
    id: 'evt-007',
    title: 'Highway 71 Expansion — Construction Begins',
    category: 'construction',
    status: 'live',
    location: 'Wadena to Bemidji, MN',
    region: 'Northern Minnesota',
    date: 'Apr 1 – Oct 31, 2026',
    description: 'MnDOT begins a 7-month expansion of Highway 71, adding passing lanes and rest stops. Increased traffic diversion expected through alternate routes, creating new OOH exposure windows.',
    impactRadius: '120 miles',
    footfallEstimate: '8,400 vehicles/day (diverted)',
    source: 'MnDOT Project Portal',
    mappedVerticals: ['automotive', 'qsr', 'tourism', 'energy'],
    mappedBrands: [
      { name: 'O\'Reilly Auto Parts', logo: 'OR', vertical: 'Automotive', fitScore: 91, reason: 'Diverted traffic creates new exposure on alternate routes' },
      { name: 'Culver\'s', logo: 'CV', vertical: 'QSR', fitScore: 88, reason: 'Detour routes pass through new food service corridors' },
      { name: 'Explore Minnesota Tourism', logo: 'EM', vertical: 'Tourism', fitScore: 85, reason: 'Redirect travelers to local attractions during detour' },
      { name: 'Xcel Energy', logo: 'XE', vertical: 'Energy', fitScore: 79, reason: 'Infrastructure project audience — utility awareness opportunity' },
    ],
  },
  {
    id: 'evt-008',
    title: 'Sanford Health — New Urgent Care Clinic',
    category: 'healthcare',
    status: 'confirmed',
    location: 'Bagley, MN',
    region: 'Northwest Minnesota',
    date: 'Jul 15, 2026',
    description: 'Sanford Health opens a new urgent care and telehealth clinic in Bagley, serving a 40-mile rural catchment area. First healthcare facility of this scale in the region.',
    impactRadius: '40 miles',
    footfallEstimate: '180 patients/day',
    source: 'Sanford Health Press Release',
    mappedVerticals: ['healthcare', 'pharmacy', 'insurance', 'grocery'],
    mappedBrands: [
      { name: 'CVS Health', logo: 'CV', vertical: 'Pharmacy', fitScore: 93, reason: 'Prescription and health product demand near new clinic' },
      { name: 'State Farm', logo: 'ST', vertical: 'Insurance', fitScore: 89, reason: 'Health insurance awareness near new healthcare access point' },
      { name: 'Essentia Health', logo: 'EH', vertical: 'Healthcare', fitScore: 85, reason: 'Competitive awareness — target same patient catchment area' },
      { name: 'Coborn\'s Grocery', logo: 'CB', vertical: 'Grocery', fitScore: 80, reason: 'Healthy food and pharmacy combo trips near clinic' },
    ],
  },
  {
    id: 'evt-009',
    title: 'Lakeland Music Festival',
    category: 'entertainment',
    status: 'confirmed',
    location: 'Walker, MN',
    region: 'Central Minnesota',
    date: 'Jul 18-20, 2026',
    description: 'Annual Lakeland Music Festival returns with 3 days of outdoor concerts, expected to draw 12,000 attendees from across Minnesota and neighboring states.',
    impactRadius: '150 miles',
    footfallEstimate: '12,000 over 3 days',
    source: 'Lakeland Music Festival Official',
    mappedVerticals: ['qsr', 'tourism', 'retail', 'events'],
    mappedBrands: [
      { name: 'Kwik Trip', logo: 'KT', vertical: 'Convenience', fitScore: 95, reason: 'Festival-goer fuel and snack runs on approach corridors' },
      { name: 'Airbnb', logo: 'AB', vertical: 'Tourism', fitScore: 92, reason: 'Festival accommodation demand — peak booking window' },
      { name: 'Live Nation', logo: 'LN', vertical: 'Events', fitScore: 88, reason: 'Cross-sell upcoming regional concerts to festival audience' },
      { name: 'Culver\'s', logo: 'CV', vertical: 'QSR', fitScore: 84, reason: 'Post-concert dining on exit corridors' },
    ],
  },
  {
    id: 'evt-010',
    title: 'New Amazon Distribution Center',
    category: 'retail',
    status: 'rumored',
    location: 'Brainerd, MN',
    region: 'Central Minnesota',
    date: 'Q3 2026 (Unconfirmed)',
    description: 'Industry sources report Amazon is evaluating a 400,000 sq ft distribution center near Brainerd. If confirmed, would bring 800+ jobs and significant daily truck traffic to the region.',
    impactRadius: '70 miles',
    footfallEstimate: '800+ workers/day',
    source: 'Brainerd Dispatch (Unconfirmed)',
    mappedVerticals: ['qsr', 'automotive', 'retail', 'finance'],
    mappedBrands: [
      { name: 'Casey\'s General Store', logo: 'CG', vertical: 'Convenience', fitScore: 90, reason: 'Workforce fuel and food demand near distribution hub' },
      { name: 'AutoZone', logo: 'AZ', vertical: 'Automotive', fitScore: 86, reason: 'Fleet vehicle maintenance demand near logistics center' },
      { name: 'Walmart', logo: 'WM', vertical: 'Retail', fitScore: 83, reason: 'Workforce grocery and essentials shopping' },
      { name: 'US Bank', logo: 'US', vertical: 'Finance', fitScore: 78, reason: 'New workforce banking and payroll services opportunity' },
    ],
  },
  {
    id: 'evt-011',
    title: 'Impending Severe Weather — Northern MN Storm System',
    category: 'disaster',
    status: 'live',
    location: 'Northern Minnesota',
    region: 'Northern Minnesota',
    date: 'Ongoing — Active Alert',
    description: 'A major storm system is tracking across Northern Minnesota, bringing the threat of severe flooding, heavy snowfall, and high winds. Emergency management agencies have issued preparedness advisories across a 12-county region. Residents are actively seeking insurance, home protection, and emergency services information.',
    impactRadius: '200 miles',
    footfallEstimate: '180,000 households at risk',
    source: 'National Weather Service / MN Emergency Management',
    mappedVerticals: ['insurance', 'home', 'homereconstruction', 'grocery', 'automotive'],
    mappedBrands: [
      { name: 'State Farm', logo: 'ST', vertical: 'Insurance', fitScore: 97, reason: 'Homeowners actively searching for coverage during active storm threat — peak intent window' },
      { name: 'Allstate', logo: 'AL', vertical: 'Insurance', fitScore: 94, reason: 'Flood and storm damage claims spike during weather events — brand recall at point of need' },
      { name: 'Menards', logo: 'MN', vertical: 'Home Protection', fitScore: 91, reason: 'Emergency supply runs — generators, tarps, sandbags, and boarding materials fly off shelves before storms hit' },
      { name: 'ServiceMaster Restore', logo: 'SM', vertical: 'Home Reconstruction', fitScore: 88, reason: 'Post-storm restoration demand surges — brand visibility before the event drives first-call advantage' },
    ],
  },
];

export const categoryConfig: Record<EventCategory, { label: string; color: string; bg: string; border: string }> = {
  retail: { label: 'Retail', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  infrastructure: { label: 'Infrastructure', color: 'text-[#A06000]', bg: 'bg-[#FFF8EC]', border: 'border-[#FEB34B]' },
  sports: { label: 'Sports', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
  education: { label: 'Education', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  entertainment: { label: 'Entertainment', color: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/20' },
  construction: { label: 'Construction', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  government: { label: 'Government', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  healthcare: { label: 'Healthcare', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  disaster: { label: 'Disaster', color: 'text-rose-600', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
};

export const statusConfig: Record<EventStatus, { label: string; color: string; dot: string }> = {
  live: { label: 'LIVE', color: 'text-green-400', dot: 'bg-green-400' },
  upcoming: { label: 'UPCOMING', color: 'text-blue-400', dot: 'bg-blue-400' },
  confirmed: { label: 'CONFIRMED', color: 'text-[#A06000]', dot: 'bg-[#FEB34B]' },
  rumored: { label: 'RUMORED', color: 'text-muted-foreground', dot: 'bg-muted-foreground' },
};
